<script>
import { provide } from 'vue';
import MacroNodes from './MacroNodes.vue';
import MacroProperties from './MacroProperties.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { validateActions } from 'dashboard/helper/validations';

export default {
  components: {
    MacroNodes,
    MacroProperties,
    NextButton,
    Icon,
  },
  props: {
    macroData: {
      type: Object,
      default: () => ({}),
    },
    canManagePublicMacros: {
      type: Boolean,
      default: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'cancel'],
  setup() {
    const v$ = useVuelidate();
    provide('v$', v$);

    return { v$ };
  },
  data() {
    return {
      macro: this.macroData,
      errors: {},
    };
  },
  computed: {
    files() {
      if (this.macro && this.macro.files) return this.macro.files;
      return [];
    },
    actionsCount() {
      return this.macro?.actions?.length || 0;
    },
  },
  watch: {
    $route: {
      handler() {
        this.resetValidation();
      },
      immediate: true,
    },
    macroData: {
      handler() {
        this.macro = this.macroData;
      },
      immediate: true,
    },
  },
  validations: {
    macro: {
      name: {
        required,
      },
      visibility: {
        required,
      },
    },
  },
  methods: {
    removeObjectProperty(obj, keyToRemove) {
      return Object.fromEntries(
        Object.entries(obj).filter(([key]) => key !== keyToRemove)
      );
    },
    updateName(value) {
      this.macro.name = value;
    },
    updateVisibility(value) {
      this.macro.visibility = value;
    },
    appendNode() {
      this.macro.actions.push({
        action_name: 'assign_team',
        action_params: [],
      });
    },
    deleteNode(index) {
      // remove that index specifically
      // so that the next item does not get marked invalid
      this.errors = this.removeObjectProperty(this.errors, `action_${index}`);
      this.macro.actions.splice(index, 1);
    },
    submit() {
      this.errors = validateActions(this.macro.actions);
      if (Object.keys(this.errors).length !== 0) return;

      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.$emit('submit', this.macro);
    },
    resetNode(index) {
      // remove that index specifically
      // so that the next item does not get marked invalid
      this.errors = this.removeObjectProperty(this.errors, `action_${index}`);
      this.macro.actions[index].action_params = [];
    },
    resetValidation() {
      this.errors = {};
      this.v$?.$reset?.();
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0">
    <div
      class="flex flex-col flex-1 min-h-0 gap-6 pb-4 lg:flex-row lg:overflow-hidden"
    >
      <!-- Fluxo de ações: cartão próprio, com o fundo pontilhado do construtor -->
      <section
        class="flex flex-col flex-1 min-w-0 overflow-hidden border rounded-xl border-n-weak bg-n-solid-1"
      >
        <header
          class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-n-weak"
        >
          <div class="flex items-center gap-2 min-w-0">
            <Icon
              icon="i-hugeicons-flow-square"
              class="size-4 text-n-slate-11"
            />
            <h2 class="m-0 text-heading-3 text-n-slate-12">
              {{ $t('MACROS.EDITOR.FLOW_TITLE') }}
            </h2>
          </div>
          <span class="text-label-small text-n-slate-11">
            {{ $t('MACROS.EDITOR.FLOW_COUNT', { count: actionsCount }) }}
          </span>
        </header>
        <div
          class="flex-1 px-4 py-4 overflow-y-auto macro-gradient-radial dark:macro-dark-gradient-radial macro-gradient-radial-size"
        >
          <div :inert="readOnly" :class="{ 'opacity-75': readOnly }">
            <MacroNodes
              v-model="macro.actions"
              :files="files"
              :errors="errors"
              @add-new-node="appendNode"
              @delete-node="deleteNode"
              @reset-action="resetNode"
            />
          </div>
        </div>
      </section>

      <!-- Propriedades: coluna lateral, rolagem própria -->
      <aside class="w-full lg:w-[22rem] lg:shrink-0 lg:overflow-y-auto">
        <div class="p-4 border rounded-xl border-n-weak bg-n-solid-1">
          <div class="flex items-center gap-2 pb-4 mb-4 border-b border-n-weak">
            <Icon
              icon="i-hugeicons-settings-01"
              class="size-4 text-n-slate-11"
            />
            <h2 class="m-0 text-heading-3 text-n-slate-12">
              {{ $t('MACROS.EDITOR.PROPERTIES_TITLE') }}
            </h2>
          </div>
          <MacroProperties
            :macro-name="macro.name"
            :macro-visibility="macro.visibility"
            :can-manage-public-macros="canManagePublicMacros"
            :read-only="readOnly"
            @update:name="updateName"
            @update:visibility="updateVisibility"
          />
        </div>
      </aside>
    </div>

    <!-- Ações sempre visíveis, fora das áreas roláveis -->
    <footer
      class="flex flex-wrap items-center justify-end gap-2 py-3 border-t border-n-weak"
    >
      <NextButton
        faded
        slate
        type="button"
        :label="$t('MACROS.EDITOR.CANCEL')"
        @click="$emit('cancel')"
      />
      <NextButton
        blue
        solid
        type="button"
        :label="$t('MACROS.HEADER_BTN_TXT_SAVE')"
        :disabled="readOnly"
        @click="submit"
      />
    </footer>
  </div>
</template>

<style scoped>
@tailwind components;

@layer components {
  .macro-gradient-radial {
    background-image: radial-gradient(#ebf0f5 1.2px, transparent 0);
  }

  .macro-dark-gradient-radial {
    background-image: radial-gradient(#293f51 1.2px, transparent 0);
  }

  .macro-gradient-radial-size {
    background-size: 1rem 1rem;
  }
}
</style>
